import React, { useState } from "react";
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  Modal,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  App,
} from "antd";
import { EditOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import PageHeader from "../../../components/PageHeader";
import ActivityIndicator from "../../../components/ActivityIndicator";
import {
  AppVersionEntry,
  AppVersionPlatform,
  useGetAppVersionsQuery,
  useUpdateAppVersionMutation,
} from "../../../store/api/appVersionApi";
import {
  isVersionEditable,
  mandatorySummary,
  versionSourceFor,
  versionSourceHint,
} from "./AppVersion.utils";

const { Text, Paragraph } = Typography;

const GENERIC_ERROR = "No se pudo guardar. Intenta nuevamente.";

/** Reads the backend's message out of an RTK Query error, if it has one. */
const errorMessage = (error: unknown): string => {
  const data = (error as { data?: { message?: string | string[] } } | undefined)
    ?.data;
  const message = data?.message;

  if (Array.isArray(message)) {
    return message[0] ?? GENERIC_ERROR;
  }

  return message || GENERIC_ERROR;
};

/**
 * Human-readable name per platform. Says which store the row governs, because
 * that is what the person editing needs to know — the enum value alone doesn't
 * tell them where the update button lands.
 */
const PLATFORM_LABELS: Record<AppVersionPlatform, string> = {
  IOS: "Alumnos · iPhone / iPad (App Store)",
  ANDROID: "Alumnos · Android (Google Play)",
  PROFESORES_IOS: "Maestros · iPhone / iPad (App Store)",
  PROFESORES_ANDROID: "Maestros · Android (Google Play)",
  DESKTOP_MAC: "Escritorio Mac (Mac App Store)",
  DESKTOP_WINDOWS: "Escritorio Windows (instalador .exe)",
};

/** Short tag next to the version, saying who keeps that number up to date. */
const SOURCE_TAGS = {
  store: { color: "green", text: "Automática" },
  device: { color: "blue", text: "La resuelve el dispositivo" },
  manual: { color: "orange", text: "Manual" },
} as const;

/**
 * Mirrors the backend validation (`create-app-version.dto.ts`). Duplicated on
 * purpose: catching it here explains the problem next to the field instead of
 * bouncing a 400 back from the server.
 */
const VERSION_PATTERN = /^\d+(\.\d+)*(-[0-9A-Za-z.-]+)?$/;
const STORE_URL_PATTERN = /^$|^(https:\/\/|macappstore:\/\/|ms-windows-store:)/;

interface FormValues {
  version: string;
  minVersion: string;
  storeUrl: string;
  showModal: boolean;
  forceUpdate: boolean;
}

/**
 * Published-version table (SCRUM-720).
 *
 * Each row tells the installed apps which version is live in their store. A
 * client that finds a version NEWER than its own shows the update notice; on
 * desktop that notice blocks the app. Nothing here pushes an update by itself:
 * it only changes what the apps are told.
 */
const AppVersion: React.FC = () => {
  const { notification } = App.useApp();
  const [form] = Form.useForm<FormValues>();
  const [editing, setEditing] = useState<AppVersionEntry | null>(null);
  const {
    data: entries,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useGetAppVersionsQuery();
  const [updateAppVersion, { isLoading: isSaving }] =
    useUpdateAppVersionMutation();

  // Watched so the warning inside the modal describes what the CURRENT form
  // values will do, not what the saved row does.
  const watchedShowModal = Form.useWatch("showModal", form);
  const watchedForceUpdate = Form.useWatch("forceUpdate", form);
  const watchedMinVersion = Form.useWatch("minVersion", form);
  const watchedStoreUrl = Form.useWatch("storeUrl", form);

  const onSave = async (values: FormValues) => {
    if (!editing) return;
    try {
      await updateAppVersion({
        platform: editing.platform,
        body: {
          // Only sent for the rows a person still owns. Posting it for a
          // resolved platform would push a hand-typed number the backend is
          // about to overwrite from the store anyway.
          ...(isVersionEditable(editing.platform)
            ? { version: values.version.trim() }
            : {}),
          minVersion: values.minVersion?.trim() || null,
          storeUrl: values.storeUrl?.trim() ?? "",
          showModal: values.showModal,
          forceUpdate: values.forceUpdate,
        },
      }).unwrap();
      notification.success({
        message: "Configuración guardada",
        description: `${PLATFORM_LABELS[editing.platform]}: ${mandatorySummary({
          minVersion: values.minVersion,
          forceUpdate: values.forceUpdate,
        })}`,
      });
      setEditing(null);
    } catch (error) {
      notification.error({
        message: "No se pudo guardar",
        description: errorMessage(error),
      });
    }
  };

  const columns = [
    {
      title: "Plataforma",
      dataIndex: "platform",
      key: "platform",
      render: (platform: AppVersionPlatform) => (
        <Text strong>{PLATFORM_LABELS[platform] ?? platform}</Text>
      ),
    },
    {
      title: "Versión publicada",
      key: "version",
      render: (_: unknown, entry: AppVersionEntry) => {
        const source = SOURCE_TAGS[versionSourceFor(entry.platform)];
        return (
          <Space size={4}>
            <Tag>{entry.version}</Tag>
            <Tag color={source.color}>{source.text}</Tag>
          </Space>
        );
      },
    },
    {
      title: "Mínima obligatoria",
      dataIndex: "minVersion",
      key: "minVersion",
      render: (minVersion: string | null) =>
        minVersion?.trim() ? (
          <Tag color="red">{minVersion}</Tag>
        ) : (
          <Text type="secondary">Ninguna</Text>
        ),
    },
    {
      title: "Aviso",
      key: "showModal",
      render: (_: unknown, entry: AppVersionEntry) => {
        if (!entry.showModal) return <Tag>No avisa</Tag>;
        // showModal on but nowhere to send the user: the client stays silent
        // rather than open an empty link, so surface that here.
        if (!entry.storeUrl.trim()) return <Tag color="orange">Sin enlace</Tag>;
        return <Tag color="green">Avisa</Tag>;
      },
    },
    {
      title: "Bloqueo total",
      dataIndex: "forceUpdate",
      key: "forceUpdate",
      render: (forceUpdate: boolean) =>
        forceUpdate ? (
          <Tag color="red">Obligatorio</Tag>
        ) : (
          <Tag>Se puede postergar</Tag>
        ),
    },
    {
      title: "Enlace a la tienda",
      dataIndex: "storeUrl",
      key: "storeUrl",
      render: (storeUrl: string) =>
        storeUrl.trim() ? (
          <a href={storeUrl} target="_blank" rel="noopener noreferrer">
            Ver ficha
          </a>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: "Último cambio",
      dataIndex: "updated_at",
      key: "updated_at",
      render: (updatedAt: string) => dayjs(updatedAt).format("DD/MM/YYYY HH:mm"),
    },
    {
      title: "Acciones",
      key: "actions",
      align: "right" as const,
      render: (_: unknown, entry: AppVersionEntry) => (
        <Button
          type="text"
          icon={<EditOutlined />}
          onClick={() => setEditing(entry)}
        >
          Editar
        </Button>
      ),
    },
  ];

  if (isLoading) {
    return <ActivityIndicator />;
  }

  return (
    <>
      <PageHeader title="Versión de la app" />

      {isError ? (
        <Alert
          type="error"
          showIcon
          message="No se pudo cargar la tabla de versiones."
          action={
            <Button size="small" onClick={() => refetch()}>
              Reintentar
            </Button>
          }
        />
      ) : (
        <Card>
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            <b>La versión publicada ya no se carga a mano.</b> La resuelve el
            App Store en cada consulta, el release del instalador en Windows, o
            el propio dispositivo en Android, así que publicar la versión nueva
            alcanza para que el aviso aparezca.
            <br />
            Lo único que se decide acá es la <b>política</b>: hasta qué versión
            se tolera seguir usando la app. Ninguna tienda puede saber eso.
            Cambiar estos valores no actualiza a nadie: sólo cambia lo que las
            apps ven.
          </Paragraph>
          <Table
            style={{ marginTop: 16 }}
            size="small"
            rowKey="id"
            columns={columns}
            dataSource={entries ?? []}
            loading={isFetching}
            pagination={false}
            locale={{ emptyText: "Todavía no hay versiones configuradas" }}
          />
        </Card>
      )}

      <Modal
        open={!!editing}
        title={editing ? PLATFORM_LABELS[editing.platform] : ""}
        onCancel={() => setEditing(null)}
        onOk={() => form.submit()}
        okText="Guardar"
        cancelText="Cancelar"
        confirmLoading={isSaving}
        destroyOnClose
        forceRender={false}
      >
        {editing && (
          <Form
            form={form}
            layout="vertical"
            requiredMark={false}
            preserve={false}
            initialValues={{
              version: editing.version,
              minVersion: editing.minVersion ?? "",
              storeUrl: editing.storeUrl,
              showModal: editing.showModal,
              forceUpdate: editing.forceUpdate,
            }}
            onFinish={onSave}
          >
            <Form.Item
              name="version"
              label="Versión publicada"
              extra={versionSourceHint(editing.platform)}
              rules={
                isVersionEditable(editing.platform)
                  ? [
                      { required: true, message: "Ingresa una versión" },
                      {
                        pattern: VERSION_PATTERN,
                        message:
                          "Debe ser un número con puntos, por ejemplo 1.2.0",
                      },
                    ]
                  : []
              }
            >
              <Input
                placeholder="1.2.0"
                disabled={!isVersionEditable(editing.platform)}
              />
            </Form.Item>

            <Form.Item
              name="minVersion"
              label="Versión mínima obligatoria"
              extra="El piso por debajo del cual el aviso no se puede posponer. Vacío = nadie queda bloqueado. Es lo único que ninguna tienda puede decidir por nosotros."
              rules={[
                {
                  pattern: VERSION_PATTERN,
                  message:
                    "Debe ser un número con puntos, por ejemplo 1.2.0",
                },
              ]}
            >
              <Input placeholder="Sin mínimo" allowClear />
            </Form.Item>

            <Form.Item
              name="storeUrl"
              label="Enlace a la tienda"
              extra="A dónde lleva el botón “Actualizar ahora”. Vacío = la app no avisa nada."
              rules={[
                {
                  pattern: STORE_URL_PATTERN,
                  message:
                    "Debe empezar con https:// (o un esquema de tienda), o quedar vacío",
                },
              ]}
            >
              <Input placeholder="https://apps.apple.com/..." allowClear />
            </Form.Item>

            <Space size="large">
              <Form.Item
                name="showModal"
                label="Mostrar el aviso de actualización"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
              <Form.Item
                name="forceUpdate"
                label="Obligatorio"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Space>

            <Alert
              type={watchedShowModal ? "warning" : "info"}
              showIcon
              message={
                !watchedShowModal
                  ? "Con el aviso apagado, esta plataforma no va a mostrar nada, sin importar la versión."
                  : !watchedStoreUrl?.trim()
                    ? "El aviso está encendido pero no hay enlace a la tienda, así que la app va a quedarse callada."
                    : mandatorySummary({
                        minVersion: watchedMinVersion,
                        forceUpdate: watchedForceUpdate,
                      })
              }
            />
          </Form>
        )}
      </Modal>
    </>
  );
};

export default AppVersion;
